import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion as Motion, AnimatePresence } from "framer-motion";
import { useDispatch } from "react-redux";
import { DateTime } from "luxon";
import { DEFAULT_TIMEZONE } from "@/utils/dateHelpers";
import QRCode from "react-qr-code";

// Parsea fechas UTC tanto en formato SQL ("2026-03-30 18:30:00")
// como ISO con Z ("2026-03-30T18:30:00.000Z") que devuelve mysql2 vía JSON.
function parseUTC(val, tz) {
  if (!val) return null;
  let dt = DateTime.fromSQL(val, { zone: "utc" });
  if (dt.isValid) return dt.setZone(tz);
  dt = DateTime.fromISO(val, { zone: "utc" });
  return dt.isValid ? dt.setZone(tz) : null;
}
import {
  toggleHabilitarPuerta,
  extenderTiempoPuerta,
} from "@/features/puerta/puertaThunks";
import {
  actualizarPuerta,
  setLoadingPuertas,
} from "@/features/puerta/puertaSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export default function CardInfoPuerta({
  puerta,
  uidEmpresa,
  fechaHoraFinEvento,
  zonaHoraria,
}) {
  const dispatch = useDispatch();

  const eventFinishDT = parseUTC(fechaHoraFinEvento, zonaHoraria || DEFAULT_TIMEZONE);
  const isEventActive = eventFinishDT ? DateTime.now() < eventFinishDT : false;

  const [isTogglingHabilitado, setIsTogglingHabilitado] = useState(false);
  const [isExtendiendoTiempo, setIsExtendiendoTiempo] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showQR, setShowQR] = useState(false);

  // Estado para controlar la expiración
  const [isExpired, setIsExpired] = useState(false);
  const [timeUntilExpiry, setTimeUntilExpiry] = useState("");

  // Verificar si el tiempo de validación ha expirado
  useEffect(() => {
    const tz = zonaHoraria || DEFAULT_TIMEZONE;

    const checkExpiry = () => {
      if (!puerta.validateTicketsFinishTime) return;

      const finishDT = parseUTC(puerta.validateTicketsFinishTime, tz);
      if (!finishDT) return;

      const nowDT = DateTime.now().setZone(tz);
      const expired = nowDT > finishDT;
      setIsExpired(expired);

      if (!expired) {
        const diff = finishDT.diff(nowDT, ['hours', 'minutes']);
        const hours = Math.floor(diff.hours);
        const minutes = Math.floor(diff.minutes % 60);

        if (hours > 24) {
          const days = Math.floor(hours / 24);
          setTimeUntilExpiry(`${days} día(s)`);
        } else if (hours > 0) {
          setTimeUntilExpiry(`${hours}h ${minutes}m`);
        } else {
          setTimeUntilExpiry(`${minutes} minuto(s)`);
        }
      } else {
        setTimeUntilExpiry("Expirado");
      }
    };

    checkExpiry();
    const interval = setInterval(checkExpiry, 60000);
    return () => clearInterval(interval);
  }, [puerta.validateTicketsFinishTime, zonaHoraria]);

  // Verificar si el código de activación ha expirado
  const isActivationCodeExpired = () => {
    if (!puerta.validationCodeExpireDate) return false;
    const tz = zonaHoraria || DEFAULT_TIMEZONE;
    const expireDT = parseUTC(puerta.validationCodeExpireDate, tz);
    if (!expireDT) return false;
    return DateTime.now().setZone(tz) > expireDT;
  };

  // Manejar toggle de habilitado
  const handleToggleHabilitado = async () => {
    if (isExpired) {
      setError(
        "No se puede cambiar el estado porque el tiempo de validación ha expirado",
      );
      setTimeout(() => setError(""), 3000);
      return;
    }

    setIsTogglingHabilitado(true);
    setError("");
    dispatch(setLoadingPuertas(true));

    try {
      const result = await dispatch(
        toggleHabilitarPuerta({
          uid: puerta.uid,
          habilitado: !puerta.habilitado,
          uidEmpresa,
        }),
      ).unwrap();

      if (result.puerta) {
        dispatch(actualizarPuerta(result.puerta));
      }

      setSuccessMessage(result.message || "Estado actualizado exitosamente");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error al cambiar estado:", error);
      setError(
        typeof error === "string"
          ? error
          : "Error al cambiar el estado de la puerta",
      );
      setTimeout(() => setError(""), 3000);
    } finally {
      setIsTogglingHabilitado(false);
      dispatch(setLoadingPuertas(false));
    }
  };

  // Manejar extensión de tiempo
  const handleExtenderTiempo = async () => {
    setIsExtendiendoTiempo(true);
    setError("");
    dispatch(setLoadingPuertas(true));

    try {
      const result = await dispatch(
        extenderTiempoPuerta({
          uid: puerta.uid,
          uidEmpresa,
        }),
      ).unwrap();

      if (result.puerta) {
        dispatch(actualizarPuerta(result.puerta));
      }

      setSuccessMessage(
        result.message || "Tiempo de código extendido exitosamente",
      );
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error al extender tiempo:", error);
      setError(
        typeof error === "string"
          ? error
          : "Error al extender el tiempo del código",
      );
      setTimeout(() => setError(""), 3000);
    } finally {
      setIsExtendiendoTiempo(false);
      dispatch(setLoadingPuertas(false));
    }
  };

  // Formatear fecha para mostrar
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const tz = zonaHoraria || DEFAULT_TIMEZONE;
    const dt = parseUTC(dateString, tz);
    if (!dt) return "N/A";
    return dt.toFormat("dd/MM/yyyy HH:mm");
  };
  return (
    <div
      className={`p-4 rounded-lg border ${
        isExpired
          ? "bg-gray-800 bg-opacity-50 border-gray-600"
          : "bg-aw-bluedark border-aw-green"
      } shadow-md`}
    >
      {/* Header con nombre y estado */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-bold text-white">{puerta.name}</h3>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-xs font-bold text-gray-400">Mostrar Código QR:</h3>
            <button
              onClick={() => setShowQR(true)}
              className="flex items-center gap-1 px-2 py-1 bg-aw-green bg-opacity-20 border border-aw-green text-aw-bluedark rounded text-xs hover:bg-opacity-30 transition-colors hover:bg-transparent hover:text-aw-green duration-200"
              title="Ver QR"
            >
              <FontAwesomeIcon icon="fa-solid fa-qrcode" size="lg" />
            </button>
          </div>
          <p className="text-sm text-gray-400">
            <b>Encargado:</b> {puerta.encargado}
          </p>
        </div>

        {/* Switch de habilitado/deshabilitado */}
        {isEventActive ? (
          <div className="flex flex-col items-end">
            <button
              onClick={handleToggleHabilitado}
              disabled={isTogglingHabilitado || isExpired}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isExpired
                  ? "bg-gray-600 cursor-not-allowed opacity-50"
                  : puerta.habilitado
                    ? "bg-aw-green"
                    : "bg-gray-600"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  puerta.habilitado ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
            <span
              className={`text-xs mt-1 ${
                isExpired
                  ? "text-gray-500"
                  : puerta.habilitado
                    ? "text-aw-green"
                    : "text-gray-400"
              }`}
            >
              {isExpired
                ? "Expirado"
                : puerta.habilitado
                  ? "Habilitada"
                  : "Deshabilitada"}
            </span>
          </div>
        ) : (
          <div className="flex flex-col items-end">
            <span className={`text-xs mt-1 text-yellow-400`}>
              Evento finalizado
            </span>
          </div>
        )}
      </div>

      {/* Código de activación */}
      <div className="mb-3 p-3 bg-aw-bgbluedark rounded-md">
        <p className="text-xs text-gray-200 mb-1">Código de Activación</p>
        <div className="flex items-center justify-between">
          <p className="text-2xl font-mono font-bold text-aw-green tracking-wider">
            {puerta.activationCode}
          </p>
          {isActivationCodeExpired() && (
            <span className="text-xs text-red-500 bg-red-900 bg-opacity-30 px-2 py-1 rounded">
              Código expirado
            </span>
          )}
        </div>
        <p className="text-xs text-gray-300 mt-1">
          Tienes hasta{" "}
          <b className="underline">
            {formatDate(puerta.validationCodeExpireDate)}
          </b>{" "}
          para habilitar el dispositivo
        </p>
      </div>

      {/* Modal QR */}
      {createPortal(
        <AnimatePresence>
          {showQR && (
            <Motion.div
              key="modal-qr"
              initial={{ opacity: 0, top: "-100%" }}
              animate={{ opacity: 1, top: "0" }}
              exit={{ opacity: 0, top: "-100%" }}
              className="fixed inset-0 bg-aw-bluedark-30 flex items-center justify-center z-[99]"
              onClick={() => setShowQR(false)}
              aria-modal="true"
              role="dialog"
            >
              <Motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.1, opacity: 0 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="relative max-w-xs w-full mx-4"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => setShowQR(false)}
                  className="absolute top-2 right-3 text-white hover:text-gray-400 font-semibold duration-300 transition-colors z-50"
                  aria-label="Cerrar modal"
                >
                  ✕
                </button>
                <div className="bg-aw-bgbluedark rounded-lg inset-shadow-sm inset-shadow-aw-green shadow-sm shadow-aw-green p-6 flex flex-col items-center gap-4">
                  <h3 className="text-white font-bold text-lg">{puerta.name}</h3>
                  <div className="bg-white p-3 rounded-md">
                    <QRCode
                      value={`door-${puerta.activationCode}`}
                      size={200}
                      bgColor="#ffffff"
                      fgColor="#000000"
                    />
                  </div>
                  <p className="text-xs text-gray-400 text-center">
                    Código: <span className="font-mono font-bold text-aw-green">{puerta.activationCode}</span>
                  </p>
                </div>
              </Motion.div>
            </Motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Información de fechas */}
      <div className="space-y-2 mb-4">
        <div>
          <p className="text-xs text-gray-400">Tiempo límite de validación</p>
          <div className="flex items-center justify-between">
            <p className="text-sm text-white">
              {formatDate(puerta.validateTicketsFinishTime)}
            </p>
            {!isExpired && (
              <span className="text-xs text-aw-bluedark font-semibold bg-aw-green bg-opacity-20 px-2 py-1 rounded">
                {timeUntilExpiry}
              </span>
            )}
            {isExpired && (
              <span className="text-xs text-red-500 bg-red-900 bg-opacity-30 px-2 py-1 rounded">
                Tiempo expirado
              </span>
            )}
          </div>
        </div>

        <div>
          <p className="text-xs text-gray-400">Creada el</p>
          <p className="text-sm text-white">{formatDate(puerta.createdAt)}</p>
        </div>
      </div>

      {/* Botón para extender tiempo — solo visible si la puerta está deshabilitada y no expirada */}
      {isEventActive && !puerta.habilitado && !isExpired && (
        <button
          onClick={handleExtenderTiempo}
          disabled={isExtendiendoTiempo}
          className="w-full px-4 py-2 bg-aw-green bg-opacity-20 text-aw-bluedark font-semibold border border-aw-green rounded-md hover:bg-opacity-30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isExtendiendoTiempo && (
            <svg
              className="animate-spin h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          )}
          <i className="fa-solid fa-clock"></i>
          Extender código +5 min
        </button>
      )}

      {/* Mensajes de error y éxito */}
      {error && (
        <div className="mt-3 p-2 bg-red-500 bg-opacity-20 border border-red-500 rounded text-xs text-red-500">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="mt-3 p-2 bg-green-500 bg-opacity-20 border border-green-500 rounded text-xs text-green-500">
          {successMessage}
        </div>
      )}
    </div>
  );
}
